import { Urls } from '@/apiConnection/urls';

export type ResponseFamily = 'wrapped' | 'raw' | 'message-only';

export interface ResponseShape {
    family: ResponseFamily;
    /** JSON key the entity is nested under, only set for family 'wrapped'. */
    key?: string;
}

/**
 * Maps a `Urls.urls` key to the shape its store()/update() endpoint returns on the real
 * backend (confirmed by reading erp-backend's controllers) - used to synthesize a plausible
 * offline response with the same shape so existing form code (which reads `entity.<key>.id`)
 * keeps working unchanged while a write is queued.
 */
export const RESPONSE_SHAPES: Record<string, ResponseShape> = {
    documents: { family: 'wrapped', key: 'document' },
    tickets: { family: 'wrapped', key: 'ticket' },
    purchases: { family: 'wrapped', key: 'purchase' },
    clients: { family: 'wrapped', key: 'client' },
    roles: { family: 'wrapped', key: 'role' },
    products: { family: 'raw' },
    categories: { family: 'raw' },
    measurements: { family: 'raw' },
    productTypes: { family: 'raw' },
    tables: { family: 'raw' },
    // Backend gap, not fixed here (see plan): store()/update() return only a message, no
    // entity - reconciliation for these two is best-effort (no real id to map back to).
    providers: { family: 'message-only' },
    pay_methods: { family: 'message-only' },
};

/** Best-effort fallback for any entityType not in the table above. */
export const DEFAULT_RESPONSE_SHAPE: ResponseShape = { family: 'raw' };

export function getResponseShape(entityType: string): ResponseShape {
    return RESPONSE_SHAPES[entityType] ?? DEFAULT_RESPONSE_SHAPE;
}

/** Reverse-looks-up a `Urls.urls.<key>` entity type from the exact base URL ApiService was called with. */
export function resolveEntityType(url: string): string | null {
    const match = Object.entries(Urls.urls).find(([, value]) => value === url);
    return match ? match[0] : null;
}

/**
 * Fields synthesized offline that are actually computed server-side and must never be
 * trusted as final - stripped out of the echoed body so nothing downstream mistakes a
 * provisional value for a real one.
 */
export const SERVER_COMPUTED_FIELDS = ['id_by_company', 'stock', 'medium_price'];
